import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Menu } from 'lucide-react';
import { VEDA_ONLINE_DOCS_URL, getExplainersHref } from '../../constants/countryDashboard';
import GlobalHeaderMobileMenu from '../modals/GlobalHeaderMobileMenu';

const GlobalHeader = React.forwardRef(function GlobalHeader(
  {
    countryIsoCode,
    isoCode,
    githubLinks,
    showUnderstandingEnergyProfiles = false,
    onUnderstandingEnergyProfilesClick,
  },
  ref
) {
  const [showGithubDropdown, setShowGithubDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const githubDropdownRef = useRef(null);

  const handleGithubToggle = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowGithubDropdown((prev) => !prev);
  }, []);

  const handleOpenMobileMenu = useCallback(() => {
    setShowMobileMenu(true);
  }, []);

  const handleCloseMobileMenu = useCallback(() => {
    setShowMobileMenu(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (githubDropdownRef.current && !githubDropdownRef.current.contains(event.target)) {
        setShowGithubDropdown(false);
      }
    };

    if (showGithubDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showGithubDropdown]);

  const vedaIso = countryIsoCode || isoCode;
  const vedaOnlineUrl = `https://vedaonline.cloud?b=${vedaIso}`;
  const explainersHref = getExplainersHref(vedaIso);

  return (
    <div ref={ref} className="bg-vervestacks-secondary px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
      <div className="flex flex-row justify-between items-center gap-3 w-full">
        <Link
          to="/"
          className="min-w-0 flex-1 text-vervestacks-primary font-bold text-xl sm:text-2xl tracking-wide hover:opacity-80 transition-opacity truncate"
          aria-label="Go to home"
        >
          VERVESTACKS
        </Link>

        <nav className="hidden md:flex flex-wrap items-center justify-center gap-3 sm:gap-4 md:space-x-6">
          <a
            href={vedaOnlineUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-vervestacks-primary hover:underline text-sm"
          >
            Veda Online
          </a>
          <a
            href={VEDA_ONLINE_DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-vervestacks-primary hover:underline text-sm"
          >
            Documentation
          </a>
          <a href={explainersHref} className="text-vervestacks-primary hover:underline text-sm">
            Explainers
          </a>
          {githubLinks.type === 'multiple' ? (
            <div className="relative" ref={githubDropdownRef}>
              <button
                type="button"
                onClick={handleGithubToggle}
                className="text-vervestacks-primary hover:underline text-sm inline-flex items-center"
              >
                GitHub{' '}
                <ChevronDown
                  className={`h-4 w-4 ml-1 text-vervestacks-primary transition-transform ${showGithubDropdown ? 'rotate-180' : ''}`}
                />
              </button>
              {showGithubDropdown && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                  <div className="py-2">
                    {githubLinks.items.map((item) => (
                      <a
                        key={item.url}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        {item.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <a
              href={githubLinks.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-vervestacks-primary hover:underline text-sm"
            >
              GitHub
            </a>
          )}
        </nav>

        <button
          type="button"
          onClick={handleOpenMobileMenu}
          className="md:hidden inline-flex items-center justify-center p-2 text-vervestacks-primary hover:opacity-80 transition-opacity"
          aria-label="Open menu"
          aria-expanded={showMobileMenu}
        >
          <Menu className="h-6 w-6" strokeWidth={2} />
        </button>
      </div>

      <GlobalHeaderMobileMenu
        isOpen={showMobileMenu}
        onClose={handleCloseMobileMenu}
        vedaOnlineUrl={vedaOnlineUrl}
        explainersHref={explainersHref}
        githubLinks={githubLinks}
        showUnderstandingEnergyProfiles={showUnderstandingEnergyProfiles}
        onUnderstandingEnergyProfilesClick={onUnderstandingEnergyProfilesClick}
      />
    </div>
  );
});

export default React.memo(GlobalHeader);
